// Pure lighting-recipe v2 resolver (M7 Chunk 3 / WS4 / ASK-080 §A). Turns a Content
// lighting-recipe (v2) + the merchant's zoneLight knobs + the shell's LIGHT_ZONE_<name>
// markers into a normalized lighting PLAN the renderer (template-composition
// applyLightingRecipe v2) places as three.js lights + an IBL environment + contact
// shadows. Kept dependency-free (no THREE) so the resolution logic is unit-testable.
//
// Consumes Content's SHIPPED field names (confirmed 2026-06-23):
//   recipe.environment.asset    — relative path → PMREM → scene.environment (IBL)
//   recipe.shadows{enabled,type,opacity,softness}
//   recipe.zones{<name>:{key,fill,ambient}}   resolved at LIGHT_ZONE_<name> markers
//   knob  zoneLight.<name> = presetId          per-zone merchant mood override
// IBL falls back to the hub's hdri-daylight-2k.hdr until Content's curated HDRIs land
// (auto-upgrades on the next mint) — the env is a CONFORMANCE fix (glTF metals need it),
// not just polish, so a sensible default beats null.

export const DEFAULT_SHADOWS = Object.freeze({ enabled: true, type: "contact", opacity: 0.4, softness: 1.0 });
export const HUB_FALLBACK_HDRI = "hdri-daylight-2k.hdr";   // ships with the hub today

const isObj = (v) => v && typeof v === "object";
const num = (v, d) => (Number.isFinite(v) ? v : d);

/** The IBL environment for a recipe — recipe.environment.asset (2K) + .assetLod (1K, load
 * first for fast paint then swap to 2K), else the hub HDRI fallback. assetLod is graceful:
 * absent until GCG -03 → loader just goes straight to the 2K. */
export function resolveEnvironment(recipe, { fallbackAsset = HUB_FALLBACK_HDRI } = {}) {
	const env = recipe?.environment || {};
	const a = (typeof env.asset === "string" && env.asset) ? env.asset : null;
	const lod = (typeof env.assetLod === "string" && env.assetLod) ? env.assetLod : null;
	if (a) return { asset: a, assetLod: lod, source: "recipe" };
	return { asset: fallbackAsset || null, assetLod: null, source: fallbackAsset ? "fallback" : "none" };
}

/** Contact-shadow settings with sane defaults (recipe overrides per field). */
export function resolveShadows(recipe) {
	const s = isObj(recipe?.shadows) ? recipe.shadows : {};
	return {
		enabled: s.enabled !== false,
		type: s.type || DEFAULT_SHADOWS.type,
		opacity: num(s.opacity, DEFAULT_SHADOWS.opacity),
		softness: num(s.softness, DEFAULT_SHADOWS.softness),
	};
}

/**
 * Per-zone light plan. One entry per zone that has either a recipe definition or a
 * shell marker; "default" is the whole-shop fallback (no marker → position null).
 * A merchant `zoneLight.<name> = presetId` pulls that zone's mood from another recipe.
 * @param {object} recipe                      the active lighting recipe (v2)
 * @param {object} knobs                       { zoneLight?: { <name>: presetId } }
 * @param {Object<string,{x,y,z}>} zoneMarkers shell LIGHT_ZONE markers keyed by zone name
 * @param {Object<string,object>} recipesById  presetId → recipe (for zone overrides)
 * @returns {Array<{name,position,key,fill,ambient,source}>}
 */
export function resolveZonePlan(recipe, knobs = {}, zoneMarkers = {}, recipesById = {}) {
	const baseZones = isObj(recipe?.zones) ? recipe.zones : {};
	const markerNames = Object.keys(zoneMarkers || {});
	const names = Array.from(new Set([...Object.keys(baseZones), ...markerNames]));
	const out = [];
	for (const name of names) {
		const isDefault = name === "default";
		const marker = zoneMarkers[name] || null;
		if (!isDefault && !marker) continue;   // can't place a zone with no marker (except whole-shop default)

		// Flat key "zoneLight.<name>" is canonical (flat /knobs/shop model); nested fallback.
		const overrideId = knobs?.[`zoneLight.${name}`] || knobs?.zoneLight?.[name];
		const overrideRecipe = overrideId && recipesById[overrideId] ? recipesById[overrideId] : null;
		const srcZones = isObj(overrideRecipe?.zones) ? overrideRecipe.zones : baseZones;
		const cfg = srcZones[name] || baseZones[name] || baseZones.default || null;
		if (!isObj(cfg)) continue;

		out.push({
			name,
			position: marker,                     // null for the whole-shop default
			key: cfg.key || null,
			fill: cfg.fill || null,
			ambient: cfg.ambient || null,
			source: overrideRecipe ? overrideId : (recipe?.presetId || null),
		});
	}
	return out;
}

/** The full normalized plan the renderer consumes. */
export function resolveLightingPlan(recipe, knobs = {}, zoneMarkers = {}, recipesById = {}, opts = {}) {
	return {
		environment: resolveEnvironment(recipe, opts),
		shadows: resolveShadows(recipe),
		zones: resolveZonePlan(recipe, knobs, zoneMarkers, recipesById),
		hasZones: Object.keys(isObj(recipe?.zones) ? recipe.zones : {}).length > 0 || Object.keys(zoneMarkers || {}).length > 0,
	};
}
